import React from "react";
import AuditDetailsView from "../../../../Models/Audit/AuditDetailsView";
import CreateAuditScheduleLocationProps from "../../../../Models/AuditProps/CreateAuditScheduleLocationProps";
import axios from "../../../../components/Api/Axios";
import BackendServices from "../../../../components/Api/BackendService";
import downloadCertificate from "../../http/downloadCertificate";
import downloadPDF from "../../http/downloadPDF";
import { AuditUrls } from "../../index";
import {
  DropdownMenuItem,
  DropdownMenuItemType,
} from "../../../../components/DropdownMenu";
import Icon, { IconType } from "../../../../components/Icon";
import Permission from "../../../../Authentication/model/Permission";
import AuditStatus from "../../models/AuditStatus";
import { getCompanyId, getUserId, isCompanyUser, isMegatronFeatureOn, isMegatronProUser } from "../../../../Authentication/Auth";
import downloadLogos from "../../http/downloadLogos";
import { trackEvent } from "../../../../util/googletagmanager/GTM";


export default (
  menuDetails: AuditDetailsView | undefined,
  refresh: () => void,
): DropdownMenuItem[] => {
  if (!menuDetails) return [];
  return [
    getAuditScheduleMenuItem(menuDetails),
    getExpiryMenuItem(menuDetails),
    getValidationMenuItem(menuDetails, refresh),
    getDownloadDocumentMenuItem(menuDetails),
    getDownloadAsPDFMenuItem(menuDetails),
    getDownloadLogosMenuItem(menuDetails)
  ].filter((item) => item !== null) as DropdownMenuItem[];
};

const getAuditScheduleMenuItem = (
  details: AuditDetailsView
): DropdownMenuItem | null => {
  if (!details.visuals.isAbleToEdit) return null;

  const data: CreateAuditScheduleLocationProps = {
    fromAuditPage: true,
    auditId: details.id,
    standard: details.standard,
    site: details.site,
  };
  return {
    type: DropdownMenuItemType.LINK,
    state: data,
    link: AuditUrls.schedule(details.id),
    icon: <Icon type={IconType.SCHEDULE} />,
    label: "Audit Schedule",
    permissionRequired: [{ permission: Permission.SCHEDULE }],
  };
};

const getExpiryMenuItem = (
  details: AuditDetailsView
): DropdownMenuItem | null => {
  if (!details.visuals.isAbleToEdit) return null;

  const state: CreateAuditScheduleLocationProps = {
    fromAuditPage: true,
    auditId: details.id,
    standard: details?.standard,
    site: details?.site,
  };
  return {
    icon: <Icon type={IconType.EDIT} />,
    label: "Give Re-audit Status",
    type: DropdownMenuItemType.LINK,
    link: AuditUrls.expiry(details.id),
    state,
    permissionRequired: [{ permission: Permission.GENERATE }],
  };
};

const getDownloadAsPDFMenuItem = (
  mainDetails: AuditDetailsView
): DropdownMenuItem | null => {
  return {
    icon: <Icon type={IconType.DOWNLOAD} />,
    type: DropdownMenuItemType.BUTTON,
    label: "Download as PDF",
    onClick: () => {
      downloadPDF(mainDetails.id);
      const megatronProUser = getUserId() != undefined && isMegatronProUser() && isMegatronFeatureOn;
    if(megatronProUser)
     {
        trackEvent('Feature_Interaction',{
        User_Role:"Company user Pro",
        Tab_Section:"My Audits - Detailed Page",
        Type:"Button",
        Feature_Function:"Download as PDF (menu option)",
        UserInput:"Click"})
     }
    },
    permissionRequired: [{ permission: Permission.VIEW }],
  };
};

const getValidationMenuItem = (
  details: AuditDetailsView,
  refresh: () => void
): DropdownMenuItem | null => {
  if (details.recordStatus !== AuditStatus.HOLDING) return null;
  const dialogHeader = `Sure you want to validate the Audit?`;

  return {
    icon: <Icon type={IconType.SUCCESS} />,
    label: "Validate Audit",
    type: DropdownMenuItemType.BUTTON_CONFIRM,
    dialogHeader,
    declineButtonText: "Cancel",
    acceptButtonText: "Validate",
    onConfirm: () =>
      axios
        .put(
          `${BackendServices.AUDIT_SERVICE.AUDIT}/validate/holding/${details.id}`
        )
        .then(refresh),
    permissionRequired: [{ permission: Permission.VERIFY }],
  };
};


const getDownloadDocumentMenuItem = (
  mainDetails: AuditDetailsView
): DropdownMenuItem | null => {
  if (!mainDetails.hasCertificate) return null;

  return {
    icon: <Icon type={IconType.DOWNLOAD} />,
    type: DropdownMenuItemType.BUTTON,
    label: "Download Certificate",
    onClick: () => {
      downloadCertificate(mainDetails.id);
    },
    permissionRequired: [{ permission: Permission.VIEW }],
  };
};

const getDownloadLogosMenuItem = (mainDetails: AuditDetailsView): DropdownMenuItem | null => {

  const acceptedStandards = ["Agents and Brokers", "Consumer Products", "ETRS", "Food", "Gluten-Free", "Packaging Materials", "Plant Based", "START!", "Storage and Distribution"];
  const acceptedExclusions = ["none", "traded", "NA", "Not Applicable", "N/A", ""];
  
  const sanitiseExclusion = (exclusion: string | null) => {
    if(exclusion === null)
      return "";
    return exclusion.replaceAll('.', '');
  }

  // Checking if the audit is NOT owned by company user OR the user is not a company user.

  if(mainDetails.auditOwner.id !== Number(getCompanyId()) || !isCompanyUser()) return null;
  
  // Checking if Record Status is NOT Current, OR Certification Status is NOT "Certificated" OR the standards is NOT one of the acceptedStandards.

  if(mainDetails.recordStatus !== AuditStatus.CURRENT || mainDetails.certificationStatus.name !== "Certificated" || !acceptedStandards.includes(mainDetails.standard.name))
    return null;

  // Checking if the exclusion from scope matches acceptedExclusions OR exclusion is empty string when standard is Gluten-Free or Plant-Based.
  // If yes return a disabled "Download Logos" button with a hover message.
  if (
    !acceptedExclusions.some(
      (exclusion) =>
        exclusion.toLowerCase() === sanitiseExclusion(mainDetails.exclusion).toLowerCase()
    ) ||
    (mainDetails.standard.name == "Gluten-Free" &&
      !(mainDetails.exclusion === "" || mainDetails.exclusion === null)) ||
    (mainDetails.standard.name === "Plant Based" &&
      !(mainDetails.exclusion === "" || mainDetails.exclusion === null)) ||
    (mainDetails.standard.name === "ETRS" &&
      !(mainDetails.exclusion === "" || mainDetails.exclusion === null))
  ) {
    return {
      icon: <Icon type={IconType.DOWNLOAD} />,
      type: DropdownMenuItemType.BUTTON,
      label: "Download Logos",
      onClick: () => {
        return downloadLogos(mainDetails.id);
      },
      permissionRequired: [],
      disabled: true,
      onHoverText: "Logos unavailable, there is an Exclusion from Scope",
    };
  }

    // If none of the criterias match the above, then user is able to download logos.
    return {
      icon: <Icon type={IconType.DOWNLOAD}/>,
      type: DropdownMenuItemType.BUTTON,
      label: "Download Logos",
      onClick: () => {
        return downloadLogos(mainDetails.id)
      },
      permissionRequired: []
    }

}